import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '@/components/ui/searchable-select';
import type { SearchableSelectOption } from '@/components/ui/searchable-select';
import { api } from '@/lib/api';
import { tenantKeys } from '@/lib/query-keys';
import { isBctStaff, useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';

interface Tenant {
  id: string;
  name: string;
  isActive: boolean;
}

interface TenantsResponse {
  data: Tenant[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function TenantSwitcher() {
  const { t } = useTranslation('common');
  const { user } = useAuthStore();
  const { selectedTenantId, setSelectedTenantId } = useUiStore();

  const queryClient = useQueryClient();
  const isBctUser = user && isBctStaff(user.role);

  const { data: tenantsResponse, isLoading } = useQuery({
    queryKey: tenantKeys.list(),
    queryFn: async () => {
      const response = await api.get<Tenant[]>('/tenants?limit=100');
      return response as TenantsResponse;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!isBctUser, // Only fetch if BCT staff
  });

  const tenants = tenantsResponse?.data;

  const tenantOptions = useMemo<SearchableSelectOption[]>(() => {
    if (!tenants) return [{ value: 'all', label: t('tenant.allTenants') }];
    const sorted = [...tenants].sort((a, b) => a.name.localeCompare(b.name));
    return [
      { value: 'all', label: t('tenant.allTenants') },
      ...sorted.map((tenant) => ({ value: tenant.id, label: tenant.name })),
    ];
  }, [tenants, t]);

  // Only show for BCT staff
  if (!isBctUser) {
    return null;
  }

  function handleTenantChange(value: string) {
    const newTenantId = value === 'all' ? null : value;
    if (newTenantId === selectedTenantId) return;
    setSelectedTenantId(newTenantId);
    // Reset rather than invalidate, and the difference is what the reader sees. The new
    // X-Tenant-Id header is part of no query key, so an invalidated query keeps its
    // cached data while it refetches: `isLoading` stays false and every list shows the
    // previous tenant's rows under the new tenant's heading. A reset drops the data, so
    // the skeleton comes back and nothing on screen claims to belong to a tenant it does
    // not. It reaches every query on purpose — every page carries this header, not just
    // the projects — with one exception: this switcher's own tenant list. That query has
    // no tenant in its key and staff get the same list whichever tenant is selected, so
    // emptying it buys nothing and costs the control that names the tenant: for as long
    // as the new request is out, the trigger would read "Loading..." and offer no
    // options, which is the one moment you cannot switch on.
    queryClient.resetQueries({
      predicate: (query) => query.queryKey[0] !== tenantKeys.all[0],
    });
  }

  return (
    <div className="flex items-center gap-2" data-tour="tenant-switcher">
      <Building2 className="h-4 w-4 text-inherit" />
      <SearchableSelect
        value={selectedTenantId ?? 'all'}
        onValueChange={handleTenantChange}
        options={tenantOptions}
        placeholder={isLoading ? t('tenant.loading') : t('tenant.allTenants')}
        searchPlaceholder={t('actions.search')}
        isLoading={isLoading}
        loadingText={t('tenant.loading')}
        className="w-[180px] shadow-none bg-white/20 border-white/30 text-inherit hover:bg-white/30"
      />
    </div>
  );
}
